import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Trash2, X, Package, Settings, User, Mail, Search, CheckSquare, Square, Radio, Save, Ban } from "lucide-react";
import AdminLayout from "@/components/Layout";

// Müşteri iptal nedenleri — backend whitelist ile birebir aynı.
const CANCELLATION_REASONS = [
  { value: "price",     label: "Pahalı buldu",         hint: "Fiyat / değer dengesi" },
  { value: "content",   label: "İçerik yetersiz",       hint: "Kanal / VOD eksikliği" },
  { value: "buffering", label: "Buffering / kalite",    hint: "Akış sorunu, donma" },
  { value: "device",    label: "Cihaz uyumsuz",         hint: "TV / Box / IPTV oynatıcı" },
  { value: "other",     label: "Diğer",                  hint: "Serbest açıklama" },
];

export default function Customers() {
  const [data, setData] = useState([]);
  const [bouquets, setBouquets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [manageId, setManageId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // {id, name}

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/customers"), api.get("/bouquets")])
      .then(([cRes, bRes]) => {
        setData(Array.isArray(cRes.data) ? cRes.data : []);
        setBouquets(Array.isArray(bRes.data) ? bRes.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when tab regains focus
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const bouquetMap = useMemo(() => {
    const m = new Map();
    bouquets.forEach(b => m.set(b._id, b));
    return m;
  }, [bouquets]);

  const onDelete = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/customers/${id}`);
      setData((rows) => rows.filter((r) => r._id !== id));
    } catch {
      window.alert("Silinemedi.");
    }
  };

  return (
    <AdminLayout breadcrumb={["Operasyon", "Müşteriler"]} title="Müşteriler">
      <div className="p-6" data-testid="customers-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Müşteriler</h1>
          <button
            onClick={() => setAddOpen(true)}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="customers-create-btn"
          >
            <Plus size={20} /> Yeni Müşteri
          </button>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-white">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold">
              <tr>
                <th className="p-4">Ad Soyad</th>
                <th className="p-4">E-posta</th>
                <th className="p-4">Paketler</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500 font-medium">Yükleniyor...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500 font-medium">Müşteri bulunamadı.</td></tr>
              ) : (
                data.map((item) => {
                  const liveBouquets = (item.bouquet_ids || [])
                    .map(id => bouquetMap.get(id))
                    .filter(Boolean);
                  return (
                    <tr key={item._id} className="hover:bg-slate-800/30 transition-colors" data-testid={`customer-row-${item._id}`}>
                      <td className="p-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-amber-500 font-bold">
                          {(item.full_name || "—").charAt(0)}
                        </div>
                        {item.full_name}
                      </td>
                      <td className="p-4 text-gray-400 font-mono text-sm">{item.email}</td>
                      <td className="p-4" data-testid={`customer-bouquets-${item._id}`}>
                        {liveBouquets.length === 0 ? (
                          <span className="text-[11px] text-gray-600 italic">Atanmadı</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 max-w-[360px]">
                            {liveBouquets.map(b => (
                              <span
                                key={b._id}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-700/40"
                                title={`${b.channel_ids?.length || 0} kanal`}
                              >
                                <Package size={10} />
                                {b.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${item.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-700 text-gray-300'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => setManageId(item._id)}
                            className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-sm font-bold"
                            data-testid={`customer-manage-${item._id}`}
                          >
                            <Settings size={14} /> Yönet
                          </button>
                          {item.status !== 'cancelled' && (
                            <button
                              onClick={() => setCancelTarget({ id: item._id, name: item.full_name })}
                              className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm font-bold"
                              data-testid={`customer-cancel-${item._id}`}
                              title="Aboneliği iptal et (neden ile)"
                            >
                              <Ban size={14} /> İptal
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(item._id)}
                            className="inline-flex items-center gap-1.5 text-red-500 hover:text-red-400 text-sm font-bold"
                            data-testid={`customer-delete-${item._id}`}
                          >
                            <Trash2 size={14} /> Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <AddCustomerModal onClose={() => setAddOpen(false)} onCreated={load} />
      )}

      {cancelTarget && (
        <CancelCustomerModal
          target={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => { setCancelTarget(null); load(); }}
        />
      )}

      <CustomerDrawer
        customerId={manageId}
        onClose={() => setManageId(null)}
        onSaved={load}
        bouquets={bouquets}
      />
    </AdminLayout>
  );
}

/* ---------------- Add Customer Modal ---------------- */

function AddCustomerModal({ onClose, onCreated }) {
  const [full_name, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("active");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await api.post("/customers", { full_name, email, status });
      onCreated();
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Oluşturulamadı.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm grid place-items-center p-6" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-xl p-6"
        data-testid="customers-add-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">Yeni Müşteri</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="block text-xs text-gray-400 mb-1.5">Ad Soyad</span>
            <input
              type="text" required autoFocus
              value={full_name}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full h-11 px-3 rounded-md bg-slate-950 border border-slate-800 text-white focus:border-amber-500 focus:outline-none"
              data-testid="customers-add-name"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-400 mb-1.5">E-posta</span>
            <input
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 rounded-md bg-slate-950 border border-slate-800 text-white focus:border-amber-500 focus:outline-none"
              data-testid="customers-add-email"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-400 mb-1.5">Durum</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-11 px-3 rounded-md bg-slate-950 border border-slate-800 text-white focus:border-amber-500 focus:outline-none"
            >
              <option value="active">active</option>
              <option value="suspended">suspended</option>
              <option value="cancelled">cancelled</option>
            </select>
          </label>
        </div>

        {err && <p className="text-xs text-red-500 mt-3">{err}</p>}

        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-slate-700 text-white font-semibold hover:bg-slate-800">Vazgeç</button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 h-11 rounded-md bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-60"
            data-testid="customers-add-submit"
          >
            {submitting ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Cancel Customer Modal (with reason) ---------------- */

function CancelCustomerModal({ target, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) { setErr("Lütfen bir neden seçin."); return; }
    setErr("");
    setSubmitting(true);
    try {
      await api.post(`/customers/${target.id}/cancel`, {
        cancellation_reason: reason,
        note: note.trim() || undefined,
      });
      onCancelled();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "İptal edilemedi.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm grid place-items-center p-6" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-slate-900 border border-orange-700/40 rounded-2xl p-6 shadow-2xl shadow-orange-500/10"
        data-testid="customer-cancel-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Ban size={18} className="text-orange-400" /> Aboneliği İptal Et
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="text-sm text-gray-400 mb-5">
          <span className="text-amber-400 font-bold">{target.name}</span> için iptal nedenini seçin. Bu veri retention analizinde kullanılacaktır.
        </div>

        <div className="space-y-2 mb-4" data-testid="customer-cancel-reasons">
          {CANCELLATION_REASONS.map(r => {
            const isOn = reason === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setReason(r.value)}
                className={`w-full text-left p-3 rounded-xl border transition flex items-center gap-3 ${
                  isOn
                    ? 'bg-orange-500/15 border-orange-500/60 ring-1 ring-orange-500/40'
                    : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                }`}
                data-testid={`customer-cancel-reason-${r.value}`}
              >
                <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${isOn ? 'border-orange-400 bg-orange-500' : 'border-slate-700'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${isOn ? 'text-orange-300' : 'text-white'}`}>{r.label}</div>
                  <div className="text-[11px] text-gray-500">{r.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        <label className="block mb-4">
          <span className="block text-xs font-bold text-gray-500 uppercase ml-1 mb-1.5">Açıklama (opsiyonel)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Operatör notu — opsiyonel"
            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-sm focus:border-orange-500 focus:outline-none resize-none"
            data-testid="customer-cancel-note"
          />
        </label>

        {err && <p className="text-xs text-red-500 mb-3" data-testid="customer-cancel-error">{err}</p>}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-slate-700 text-white font-semibold hover:bg-slate-800"
            data-testid="customer-cancel-back"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 h-11 rounded-xl bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            data-testid="customer-cancel-submit"
          >
            <Ban size={14} />
            {submitting ? "İptal ediliyor…" : "İptal Et"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Customer Management Drawer ---------------- */

function CustomerDrawer({ customerId, onClose, onSaved, bouquets }) {
  const [mounted, setMounted] = useState(false);   // visible in DOM
  const [open, setOpen] = useState(false);         // for translate animation
  const [details, setDetails] = useState(null);
  const [tab, setTab] = useState('info');
  const [loading, setLoading] = useState(false);

  // Mount/unmount with animation
  useEffect(() => {
    if (customerId) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
      setTab('info');
    } else if (mounted) {
      setOpen(false);
      const t = setTimeout(() => setMounted(false), 320);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Fetch details when customerId changes
  useEffect(() => {
    if (!customerId) { setDetails(null); return; }
    setLoading(true);
    api.get(`/customers/${customerId}/details`)
      .then(res => setDetails(res.data))
      .catch(err => {
        console.error('Detay yüklenemedi:', err);
        setDetails(null);
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  const reload = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    api.get(`/customers/${customerId}/details`)
      .then(res => setDetails(res.data))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[80]" data-testid="customer-drawer">
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        data-testid="customer-drawer-overlay"
      />

      {/* Drawer panel */}
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-2xl bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-amber-500 font-bold text-lg shrink-0">
              {(details?.customer?.full_name || '—').charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-white truncate" data-testid="drawer-customer-name">
                {details?.customer?.full_name || 'Yükleniyor…'}
              </div>
              <div className="text-xs text-gray-500 font-mono truncate">{details?.customer?.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded hover:bg-slate-800 transition" data-testid="drawer-close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950/50">
          {[
            { id: 'info', label: 'Bilgi', icon: User },
            { id: 'bouquets', label: 'Paketler', icon: Package, count: details?.bouquets?.length },
            { id: 'lines', label: 'Hatlar', icon: Radio, count: details?.lines?.length },
          ].map(t => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold transition border-b-2 ${
                  isActive
                    ? 'text-amber-400 border-amber-500 bg-slate-900'
                    : 'text-gray-500 border-transparent hover:text-white hover:bg-slate-800/40'
                }`}
                data-testid={`drawer-tab-${t.id}`}
              >
                <Icon size={16} />
                {t.label}
                {t.count != null && t.count > 0 && (
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${isActive ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-800 text-gray-400'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && !details ? (
            <div className="p-12 text-center text-gray-500 italic">Yükleniyor…</div>
          ) : !details ? (
            <div className="p-12 text-center text-gray-500 italic">Detaylar yüklenemedi.</div>
          ) : (
            <>
              {tab === 'info'     && <InfoTab     details={details} onSaved={() => { reload(); onSaved && onSaved(); }} />}
              {tab === 'bouquets' && <BouquetsTab details={details} bouquets={bouquets} onSaved={() => { reload(); onSaved && onSaved(); }} />}
              {tab === 'lines'    && <LinesTab    lines={details.lines} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Drawer Tabs ---------------- */

function InfoTab({ details, onSaved }) {
  const c = details.customer;
  const [form, setForm] = useState({ full_name: c.full_name, email: c.email, status: c.status });
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // Reset when customer changes
  useEffect(() => {
    setForm({ full_name: c.full_name, email: c.email, status: c.status });
  }, [c._id, c.full_name, c.email, c.status]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSavedOk(false);
    try {
      await api.put(`/customers/${c._id}`, form);
      setSavedOk(true);
      onSaved();
      setTimeout(() => setSavedOk(false), 2000);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 space-y-5" data-testid="drawer-info-form">
      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase ml-1 mb-2">Ad Soyad</label>
        <div className="relative">
          <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            required
            type="text"
            value={form.full_name}
            onChange={e => setForm({ ...form, full_name: e.target.value })}
            className="w-full pl-10 h-12 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-amber-500 focus:outline-none"
            data-testid="drawer-input-name"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase ml-1 mb-2">E-posta</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            required
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            className="w-full pl-10 h-12 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-amber-500 focus:outline-none font-mono text-sm"
            data-testid="drawer-input-email"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase ml-1 mb-2">Durum</label>
        <div className="grid grid-cols-3 gap-2">
          {['active', 'suspended', 'cancelled'].map(s => {
            const isOn = form.status === s;
            const tone = s === 'active'
              ? isOn ? 'bg-green-500/15 text-green-400 border-green-700/50' : 'bg-slate-950 text-gray-500 border-slate-800'
              : s === 'suspended'
              ? isOn ? 'bg-amber-500/15 text-amber-400 border-amber-700/50' : 'bg-slate-950 text-gray-500 border-slate-800'
              : isOn ? 'bg-red-500/15 text-red-400 border-red-700/50' : 'bg-slate-950 text-gray-500 border-slate-800';
            return (
              <button
                type="button"
                key={s}
                onClick={() => setForm({ ...form, status: s })}
                className={`h-12 rounded-xl border text-xs font-bold uppercase tracking-wider transition ${tone}`}
                data-testid={`drawer-status-${s}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="pt-3">
        <button
          type="submit"
          disabled={saving}
          className="w-full h-12 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-60 transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
          data-testid="drawer-info-save"
        >
          <Save size={16} />
          {saving ? 'Kaydediliyor…' : savedOk ? 'Kaydedildi ✓' : 'Bilgileri Güncelle'}
        </button>
      </div>
    </form>
  );
}

function BouquetsTab({ details, bouquets, onSaved }) {
  const c = details.customer;
  const [selected, setSelected] = useState(new Set(c.bouquet_ids || []));
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset when customer changes
  useEffect(() => {
    setSelected(new Set(c.bouquet_ids || []));
    setQuery('');
  }, [c._id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bouquets;
    return bouquets.filter(b => b.name?.toLowerCase().includes(q));
  }, [bouquets, query]);

  const toggle = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const initial = useMemo(() => new Set(c.bouquet_ids || []), [c.bouquet_ids]);
  const dirty = useMemo(() => {
    if (initial.size !== selected.size) return true;
    for (const id of selected) if (!initial.has(id)) return true;
    return false;
  }, [initial, selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/customers/${c._id}/bouquets`, { bouquet_ids: Array.from(selected) });
      onSaved();
    } catch (err) {
      alert('Paketler güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 flex flex-col h-full gap-4" data-testid="drawer-bouquets-tab">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          <span className="text-amber-400">{selected.size}</span> seçili / {bouquets.length} paket
        </span>
        {bouquets.length === 0 && (
          <span className="text-[10px] text-gray-600 italic">Önce /admin/bouquets sayfasından paket oluşturun</span>
        )}
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Paket ara..."
          className="w-full pl-11 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none text-sm"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid="drawer-bouquet-search"
        />
      </div>

      <div className="flex-1 overflow-y-auto bg-slate-950 border border-slate-800 rounded-xl divide-y divide-slate-800/60 min-h-[200px]" data-testid="drawer-bouquet-list">
        {bouquets.length === 0 ? (
          <div className="p-8 text-center text-gray-500 italic text-sm">Henüz paket yok.</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500 italic text-sm">"{query}" için sonuç yok.</div>
        ) : (
          filtered.map(b => {
            const checked = selected.has(b._id);
            return (
              <button
                type="button"
                key={b._id}
                onClick={() => toggle(b._id)}
                className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/40 transition ${checked ? 'bg-amber-500/5' : ''}`}
                data-testid={`drawer-bouquet-${b._id}`}
              >
                {checked
                  ? <CheckSquare size={18} className="text-amber-400 shrink-0" />
                  : <Square size={18} className="text-gray-600 shrink-0" />}
                <Package size={14} className="text-amber-500/60 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{b.name}</div>
                  <div className="text-[10px] font-mono text-gray-500">
                    {(b.channel_ids?.length || 0)} kanal
                  </div>
                </div>
                {!b.is_active && (
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">Pasif</span>
                )}
              </button>
            );
          })
        )}
      </div>

      <button
        onClick={save}
        disabled={!dirty || saving}
        className="w-full h-12 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
        data-testid="drawer-bouquets-save"
      >
        <Save size={16} />
        {saving ? 'Kaydediliyor…' : dirty ? 'Paketleri Güncelle' : 'Değişiklik yok'}
      </button>
    </div>
  );
}

function LinesTab({ lines }) {
  return (
    <div className="p-6" data-testid="drawer-lines-tab">
      {lines.length === 0 ? (
        <div className="p-12 text-center text-gray-500 italic" data-testid="drawer-lines-empty">
          Bu müşteriye ait hat yok. <br />
          <span className="text-[11px] text-gray-700">/admin/lines sayfasından hat eklenebilir.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {lines.map(l => (
            <div key={l._id} className="bg-slate-950 border border-slate-800 rounded-xl p-4 hover:border-amber-700/40 transition" data-testid={`drawer-line-${l._id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-amber-400 font-bold truncate">{l.username}</div>
                  <div className="text-[10px] text-gray-500 font-mono truncate">{l.package_name}</div>
                </div>
                <span className={`shrink-0 text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${l.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                  {l.status}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-[11px] font-mono">
                <div>
                  <div className="text-gray-500 uppercase tracking-wider mb-0.5 text-[9px]">Bağlantı</div>
                  <div className="text-white">{l.active_connections ?? 0}/{l.max_connections ?? 1}</div>
                </div>
                <div>
                  <div className="text-gray-500 uppercase tracking-wider mb-0.5 text-[9px]">Son IP</div>
                  <div className="text-gray-300 truncate">{l.last_ip || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-gray-500 uppercase tracking-wider mb-0.5 text-[9px]">Bitiş</div>
                  <div className="text-gray-300">
                    {l.expiry_date ? new Date(l.expiry_date).toLocaleDateString('tr-TR') : '—'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
